import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, Store, UserRound, Users, X } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../utils/api';
import DatePicker from './DatePicker';
import './SubscriptionExpirySetting.css';

// Site-wide switch in the settings panel: "no expiration", one per audience (members, service providers).
//   - Switch ON  -> everybody who is registered (and everybody who subscribes later) gets lifetime access.
//   - Switch OFF -> everybody gets an end date again: start date + the plan's validity.
// After that the admin can still treat ONE person differently ("Manage one person"): give lifetime access, or set an end date.
const SubscriptionExpirySetting = () => {
    const [flags, setFlags] = useState(null);          // { member, service }: is "no expiration" on for that audience
    const [audience, setAudience] = useState('member'); // which audience the open dialog is about
    const [pending, setPending] = useState(null);      // the value being switched to, while the confirm dialog is open
    const [people, setPeople] = useState(false);       // the "Manage one person" dialog is open
    const [saving, setSaving] = useState(false);
    const [subs, setSubs] = useState([]);              // the page(s) of running subscriptions loaded so far
    const [total, setTotal] = useState(0);             // how many running subscriptions this audience has in all
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [listLoading, setListLoading] = useState(false);
    const [busyId, setBusyId] = useState('');          // the row whose own button is working
    const [filter, setFilter] = useState('');
    const [from, setFrom] = useState('');             // registered from (yyyy-mm-dd)
    const [to, setTo] = useState('');                 // registered until (yyyy-mm-dd)
    const [bulkBusy, setBulkBusy] = useState(false);
    const searchTimer = useRef(null);

    useEffect(() => {
        API.get('/plans/admin/settings').then(res => setFlags({ member: !!res.data.member, service: !!res.data.service })).catch(() => setFlags({ member: false, service: false }));
    }, []);

    // one page of running subscriptions (10 at a time, searched on the server, so it stays fast with any number of records)
    const loadPage = async (kind, nextPage, search, range = { from, to }) => {
        setListLoading(true);
        try {
            const { data } = await API.get('/subscriptions/admin/running', { params: { audience: kind, page: nextPage, limit: 10, search, from: range.from || undefined, to: range.to || undefined } });
            setSubs(data.subscriptions || []);
            setTotal(data.total || 0);
            setPage(nextPage);
            setTotalPages(data.totalPages || 1);
        } catch {
            setSubs([]); setTotal(0); setTotalPages(1);
        } finally {
            setListLoading(false);
        }
    };

    const openConfirm = kind => {
        setAudience(kind);
        setPending(!flags[kind]);
        loadPage(kind, 1, ''); // only to show how many people the switch will change
    };

    const openPeople = kind => {
        setAudience(kind);
        setFilter('');
        setFrom('');
        setTo('');
        setSubs([]);
        setPeople(true);
        loadPage(kind, 1, '', { from: '', to: '' });
    };

    const onSearch = value => {
        setFilter(value);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => loadPage(audience, 1, value.trim()), 350);
    };

    const changeRange = (nextFrom, nextTo) => {
        setFrom(nextFrom);
        setTo(nextTo);
        loadPage(audience, 1, filter.trim(), { from: nextFrom, to: nextTo });
    };

    // quick month choices: 0 = this month, 1 = last month
    const pickMonth = monthsAgo => {
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1);
        const last = new Date(now.getFullYear(), now.getMonth() - monthsAgo + 1, 0);
        const iso = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        changeRange(iso(first), iso(last));
    };

    // everyone who matches the dates / search, in one go
    const updateRange = async mode => {
        const sentence = mode === 'lifetime' ? 'lifetime access' : 'an end date (start date + plan validity)';
        if (!window.confirm(`Give ${sentence} to all ${total} ${total === 1 ? 'person' : 'people'} in this list?`)) return;
        setBulkBusy(true);
        try {
            const { data } = await API.put('/subscriptions/admin/validity-range', { audience, search: filter.trim(), from, to, mode });
            toast.success(data.message);
            loadPage(audience, page, filter.trim());
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not update this group');
        } finally {
            setBulkBusy(false);
        }
    };

    const nameOf = sub => sub.user?.name || sub.user?.businessName || 'Unknown';

    // the switch: everybody follows it
    const save = async () => {
        setSaving(true);
        try {
            const { data } = await API.put('/plans/admin/settings', { audience, noExpiry: pending, applyExisting: true });
            setFlags(prev => ({ ...prev, [audience]: pending }));
            toast.success(data.message || 'Setting saved');
            setPending(null);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not save the setting');
        } finally {
            setSaving(false);
        }
    };

    // change exactly one person, right now; nobody else is touched
    const updateOne = async (sub, nextMode) => {
        setBusyId(sub._id);
        try {
            const { data } = await API.put(`/subscriptions/admin/${sub._id}/validity`, { mode: nextMode });
            setSubs(prev => prev.map(row => row._id === sub._id ? { ...row, noExpiry: data.subscription.noExpiry, expiresAt: data.subscription.expiresAt } : row));
            toast.success(`${nameOf(sub)}: ${data.message}`);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not update this person');
        } finally {
            setBusyId('');
        }
    };

    const who = audience === 'service' ? 'service providers' : 'members';
    const AUDIENCES = [
        { id: 'member', label: 'Members', Icon: UserRound },
        { id: 'service', label: 'Service providers', Icon: Store },
    ];

    if (flags === null) return null;

    return (
        <div className="admin-theme-section">
            <span className="admin-theme-label">Subscriptions</span>
            <div className="sub-expiry-toggles">
                {AUDIENCES.map(({ id, label, Icon }) => (
                    <div key={id} className="sub-expiry-block">
                        <button type="button" className={`sub-expiry-toggle ${flags[id] ? 'is-on' : ''}`} onClick={() => openConfirm(id)} role="switch" aria-checked={flags[id]}>
                            <span className="sub-expiry-toggle__icon"><Icon size={16} /></span>
                            <span className="sub-expiry-toggle__text"><b>{label}: no expiration</b><em>{flags[id] ? 'Everyone has lifetime access' : 'Everyone ends on their plan date'}</em></span>
                            <span className="sub-expiry-toggle__switch" aria-hidden="true"><i /></span>
                        </button>
                        <button type="button" className="sub-expiry-manage" onClick={() => openPeople(id)}><Users size={13} /> Manage one person</button>
                    </div>
                ))}
            </div>

            {pending !== null && createPortal(
                <div className="sub-expiry-backdrop" data-keep-settings-open onMouseDown={event => { if (event.target === event.currentTarget && !saving) setPending(null); }}>
                    <section className="sub-expiry-dialog" role="dialog" aria-modal="true" aria-labelledby="sub-expiry-title">
                        <header>
                            <div>
                                <h3 id="sub-expiry-title">{pending ? `Turn on no expiration for ${who}?` : `Turn off no expiration for ${who}?`}</h3>
                                <p>{pending
                                    ? `All ${total} ${who} who are registered now, and everyone who subscribes later, get lifetime access.`
                                    : `All ${total} ${who} who are registered now, and everyone who subscribes later, get an end date again: start date + plan validity.`}</p>
                            </div>
                            <button type="button" onClick={() => setPending(null)} aria-label="Close" disabled={saving}><X size={17} /></button>
                        </header>
                        <p className="sub-expiry-summary" role="status">After this you can still give one person lifetime access, or set an end date for one person, with “Manage one person”.</p>
                        <footer>
                            <button type="button" onClick={() => setPending(null)} disabled={saving}>Cancel</button>
                            <button type="button" className="is-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : pending ? 'Turn on no expiration' : 'Turn off no expiration'}</button>
                        </footer>
                    </section>
                </div>,
                document.body,
            )}

            {people && createPortal(
                <div className="sub-expiry-backdrop" data-keep-settings-open onMouseDown={event => { if (event.target === event.currentTarget) setPeople(false); }}>
                    <section className="sub-expiry-dialog" role="dialog" aria-modal="true" aria-labelledby="sub-expiry-people-title">
                        <header>
                            <div>
                                <h3 id="sub-expiry-people-title">Manage one person ({who})</h3>
                                <p>The button on a row changes only that person, right away.</p>
                            </div>
                            <button type="button" onClick={() => setPeople(false)} aria-label="Close"><X size={17} /></button>
                        </header>
                        <label className="sub-expiry-search">
                            <Search size={15} />
                            <input type="search" placeholder="Search by name, mobile, email or ID" value={filter} onChange={event => onSearch(event.target.value)} />
                        </label>
                        <div className="sub-expiry-range">
                            <div className="sub-expiry-range__field"><span>Registered from</span><DatePicker value={from} max={to} placeholder="Start date" onChange={value => changeRange(value, to)} /></div>
                            <div className="sub-expiry-range__field"><span>to</span><DatePicker value={to} min={from} placeholder="End date" onChange={value => changeRange(from, value)} /></div>
                            <div className="sub-expiry-range__quick">
                                <button type="button" onClick={() => pickMonth(0)}>This month</button>
                                <button type="button" onClick={() => pickMonth(1)}>Last month</button>
                                {(from || to) && <button type="button" onClick={() => changeRange('', '')}>Clear</button>}
                            </div>
                        </div>
                        {(from || to || filter.trim()) && total > 0 && (
                            <div className="sub-expiry-bulkbar">
                                <span>All <b>{total}</b> {total === 1 ? 'person' : 'people'} in this list:</span>
                                <button type="button" disabled={bulkBusy} onClick={() => updateRange('lifetime')}>Give lifetime</button>
                                <button type="button" disabled={bulkBusy} onClick={() => updateRange('enddate')}>Set end date</button>
                            </div>
                        )}
                        <div className="sub-expiry-list">
                            {subs.map(sub => (
                                <div key={sub._id} className="sub-expiry-row">
                                    <div>
                                        <b>{nameOf(sub)}</b>
                                        <small>{sub.user?.memberId || sub.user?.providerId || sub.user?.mobile || ''} · {sub.planNameSnapshot} · {sub.noExpiry ? 'lifetime now' : `ends ${new Date(sub.expiresAt).toLocaleDateString('en-IN')}`}</small>
                                    </div>
                                    <button type="button" className="sub-expiry-rowbtn" disabled={busyId === sub._id}
                                        onClick={() => updateOne(sub, sub.noExpiry ? 'enddate' : 'lifetime')}>
                                        {busyId === sub._id ? 'Saving…' : sub.noExpiry ? 'Set end date' : 'Give lifetime'}
                                    </button>
                                </div>
                            ))}
                            {!listLoading && subs.length === 0 && <p className="sub-expiry-empty">{filter ? 'Nobody matches that search.' : 'There are no running subscriptions.'}</p>}
                            {listLoading && <p className="sub-expiry-empty">Loading…</p>}
                        </div>
                        <div className="sub-expiry-pager">
                            <button type="button" disabled={listLoading || page <= 1} onClick={() => loadPage(audience, page - 1, filter.trim())}>Previous</button>
                            <span>Page {page} of {totalPages} · {total} {total === 1 ? 'person' : 'people'}</span>
                            <button type="button" disabled={listLoading || page >= totalPages} onClick={() => loadPage(audience, page + 1, filter.trim())}>Next</button>
                        </div>
                        <footer>
                            <button type="button" className="is-primary" onClick={() => setPeople(false)}>Done</button>
                        </footer>
                    </section>
                </div>,
                document.body,
            )}
        </div>
    );
};

export default SubscriptionExpirySetting;
